import { Source, Layer } from 'react-map-gl';
import { FilteredLayer } from './FilteredLayer';
import { COLOR, makeFillColorByEstado, makeEstadoMatchExpr } from '../styles';
import { sourceProps } from './source-utils';
import type { FiltrosVisor } from '../types';

interface UsoComunLayerProps {
  url: string;
  /**
   * GeoJSON pre-enriquecido con `live_estado/live_firmado/...` (mismo
   * contrato que ParcelasLayer.geojson, generado por useParcelasLiveMerged /
   * useParcelasMergedCaptura). Si se pasa, la capa participa en el semáforo:
   * overlay de color por estado encima del fill violeta base. Sin él
   * (landing legacy), render estático violeta como antes de mig 0068.
   */
  geojson?: GeoJSON.FeatureCollection;
  /** Pinta overlay de semáforo cuando hay gestión (default true en modo enriquecido). */
  semaforo?: boolean;
  /** Filtros del visor. Solo aplican estado/municipio/anillo/ejido —
   *  `poligono` (P1/P2/PP) y `reunion_bucket` son conceptos de parcela
   *  individual y `parcela_id_in` es el recorte espacial precalculado de
   *  parcelas; ninguno tiene contraparte en uso común. */
  filtros?: FiltrosVisor;
  /** Resalta el contorno del polígono seleccionado (flyTo en MapaParcelas). */
  highlightParcelaId?: string | null;
}

/**
 * Tierras de uso común RAN. Desde mig 0068 es una capa OPERATIVA: los 30
 * polígonos tienen `parcela_id` (HUM-UC####/COR-UC####) con fila en
 * public.parcelas, así que son clickeables (MapaParcelas enruta el clic por
 * el mismo `onParcelaClick` de parcelas) y entran al semáforo.
 *
 * Decisión de diseño (RF3, opción B documentada en docs/ARCHITECTURE.md):
 * fuente y estilos SEPARADOS de ParcelasLayer — el violeta identifica el
 * uso común frente a parcelas individuales, el toggle "Uso común" del
 * toolbar conserva su semántica, y los filtros P1/P2/PP de parcelas no
 * los clasifican mal — pero el PIPELINE de gestión (enriquecimiento live,
 * click handler, captura, vistas) es el mismo.
 */
export function UsoComunLayer({
  url,
  geojson,
  semaforo = true,
  filtros,
  highlightParcelaId,
}: UsoComunLayerProps) {
  const enriched = Boolean(geojson);
  const sourceCfg = geojson
    ? { type: 'geojson' as const, data: geojson }
    : sourceProps(url);
  const filtersExpr = buildUsoComunFilters(filtros ?? {}, enriched);
  const fillColorByEstado = makeFillColorByEstado(enriched) as never;

  return (
    <Source id="uso-comun" {...sourceCfg} promoteId="parcela_id">
      <FilteredLayer
        id="uso-comun-fill"
        type="fill"
        filter={filtersExpr}
        paint={{ 'fill-color': COLOR.violet, 'fill-opacity': 0.14 }}
      />
      {/* Overlay de semáforo: solo pinta cuando hay gestión real
          (live_estado ≠ sin_prospectar). Las sin gestión conservan el
          violeta identitario — a diferencia de parcelas, donde el gris
          "sin_prospectar" es el estado base esperado. */}
      {enriched && semaforo && (
        <FilteredLayer
          id="uso-comun-fill-estado"
          type="fill"
          filter={filtersExpr}
          paint={{
            'fill-color': fillColorByEstado,
            'fill-opacity': [
              'case',
              ['==', makeEstadoMatchExpr(true) as never, 'sin_prospectar'],
              0,
              0.62,
            ] as never,
          }}
        />
      )}
      <FilteredLayer
        id="uso-comun-outline"
        type="line"
        filter={filtersExpr}
        paint={{ 'line-color': COLOR.violet, 'line-width': 2, 'line-opacity': 0.85 }}
      />
      {highlightParcelaId ? (
        <Layer
          id="uso-comun-selection-outline"
          type="line"
          filter={
            (filtersExpr != null
              ? ['all', filtersExpr, ['==', ['get', 'parcela_id'], highlightParcelaId]]
              : ['==', ['get', 'parcela_id'], highlightParcelaId]) as never
          }
          paint={{ 'line-color': COLOR.cream, 'line-width': 3.2 }}
        />
      ) : null}
    </Source>
  );
}

function buildUsoComunFilters(f: FiltrosVisor, enriched: boolean): never | undefined {
  const clauses: unknown[] = ['all'];

  if (f.estado?.length) {
    if (enriched) {
      const labels = f.estado.length === 1 ? [f.estado[0]] : [...f.estado];
      clauses.push(['match', makeEstadoMatchExpr(true), labels, true, false]);
    } else {
      // Legacy sin enriquecimiento: no hay live_estado ni feature-state en
      // esta fuente — un filtro por estado oculta toda la capa para no
      // mostrar polígonos sin clasificar como si matchearan.
      clauses.push(['==', 1, 0]);
    }
  }
  if (f.municipio?.length) {
    clauses.push(['in', ['upcase', ['to-string', ['get', 'municipio']]], ['literal', f.municipio]]);
  }
  if (f.anillo?.length) {
    clauses.push(['in', ['get', 'anillo'], ['literal', f.anillo]]);
  }
  if (f.ejido?.length) {
    clauses.push(['in', ['get', 'nom_nucleo'], ['literal', f.ejido]]);
  }
  return clauses.length > 1 ? (clauses as never) : undefined;
}
